/* Common Imports */
import { makeStyles } from "@material-ui/core/styles";
import React from "react";

/* Component Imports */
import { Typography } from "@material-ui/core";
import Button from "@material-ui/core/Button";
/* Data Imports */
import { Download } from "@mui/icons-material";
import AppConfig from "../../../AppConfig";
import HomepageDownloadModal from "../../modals/HomepageDownloadModal";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    margin: "5rem 8rem 5rem 8rem",
    [theme.breakpoints.down("sm")]: {
      margin: "2rem 2rem 2rem 2rem",
    },
  },
  sectionContainer: {
    display: "flex",
    flexDirection: "row",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column-reverse",
      justifyContent: "center",
      gap: "2rem",
    },
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    gap: "3rem",
  },
  text: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    width: "60%",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  heading: {
    margin: "0rem 0rem 1rem 0rem",
    fontWeight: 600,
    fontWeight: 600,
    color: "#0DB14B",
    fontSize: "2.125rem",
    lineHeight: "2.5rem",
    // [theme.breakpoints.down("sm")]: {
    //   fontSize: "1.75rem",
    //   lineHeight: "2.125rem",
    // },
    // [theme.breakpoints.down("xs")]: {
    //   fontSize: "1.75rem",
    //   lineHeight: "2.125rem",
    // },
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.25rem",
      lineHeight: "2rem",
    },
  },
  heading1: {
    width: "100%",
    margin: "0rem 0rem 1rem 0rem",
    fontWeight: 600,
    color: "#202020",
    fontSize: "2.125rem",
    lineHeight: "2.5rem",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.125rem",
    },
  },
  subHeading: {
    margin: "0rem 0rem 1rem 0rem",
    width: "100%",
    color: "#212121",
    fontWeight: 400,
    fontSize: "1.25rem",
    lineHeight: "2rem",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1rem",
      lineHeight: "1.625rem",
    },
  },
  illustrationSizer: {
    /* XXS breakpoint */
    [theme.breakpoints.down(theme.breakpoints.values.xs + 280)]: {
      height: "200px",
      width: "270px",
    },
    [theme.breakpoints.only("xs")]: {
      height: "320px",
      width: "350px",
    },
    [theme.breakpoints.only("sm")]: {
      height: "400px",
      width: "600px",
    },
    [theme.breakpoints.only("md")]: {
      height: "250px",
      width: "400px",
    },
    [theme.breakpoints.only("lg")]: {
      height: "550px",
      width: "550px",
    },
    [theme.breakpoints.only("xl")]: {
      height: "550px",
      width: "550px",
    },
    /* XXL breakpoint */
    [theme.breakpoints.up(theme.breakpoints.values.xl + 640)]: {
      height: "550px",
      width: "550px",
    },
    [theme.breakpoints.up(theme.breakpoints.values.xl + 1920)]: {
      height: "550px",
      width: "550px",
    },
  },
  btnRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "flex-end",
    gap: "1rem",
    [theme.breakpoints.only("xs")]: {
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
    },
  },
  primaryButton: {
    padding: "0.5rem 1rem",
    background: "#0DB14B",
    color: "#FFFFFF",
    fontSize: "1rem",
    fontWeight: "500",
    borderRadius: "0.75rem",
    textTransform: "none",
    "&:hover": {
      background: "#0DB14B",
      color: "#FFFFFF",
    },
    [theme.breakpoints.only("xs")]: {
      width: "100%",
    },
  },
  secondaryButton: {
    background: "#FFFFFF",
    color: "#0DB14B",
    border: "solid 1px #0DB14B",
    padding: "0.5rem 1rem",
    fontSize: "1rem",
    fontWeight: "500",
    textTransform: "none",
    borderRadius: "0.75rem",
    [theme.breakpoints.only("xs")]: {
      width: "100%",
    },
  },
  mobile: {
    margin: "0rem 0rem 1rem 0rem",
    [theme.breakpoints.up("sm")]: {
      display: "none",
    },
  },
  animation: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
}));

const AboutUs = () => {
  const classes = useStyles();

  const [openDownload, setOpenDownload] = React.useState(false);
  const handleClose = () => {
    setOpenDownload(false);
  };
  const handleOpen = () => {
    setOpenDownload(true);
  };
  return (
    <React.Fragment>
      <HomepageDownloadModal
        open={openDownload}
        onClose={handleClose}
        source="Download PDF"
        submitCallback={(data) => {
          setOpenDownload(false);
        }}
      />
      <div className={classes.mainContainer}>
        <div className={classes.sectionContainer}>
          <div className={classes.animation}>
            <img
              src="/images/icons/heavie/about-us.webp"
              alt={AppConfig.imageAltText}
              width={512}
              height={512}
              loading="eager"
              referrerPolicy="no-referrer"
              className={classes.illustrationSizer}
            />
          </div>
          <div className={classes.text}>
            <Typography
              variant="h1"
              component="h1"
              className={classes.heading}
            >
              About Us
            </Typography>
            <div className={classes.mobile}>
              <img
                src="/images/icons/heavie/about-us.webp"
                alt={AppConfig.imageAltText}
                width={512}
                height={512}
                loading="eager"
                referrerPolicy="no-referrer"
                className={classes.illustrationSizer}
              />
            </div>
            <Typography
              variant="body2"
              component="span"
              className={classes.subHeading}
            >
              Sentient was born to create sustainable solutions to the most profound challenges of real estate. We
              develop conscious structures across all verticals - residential, commercial, and industrial - with deep
              respect for nature and a commitment to future generations. With three generations of experience behind us,
              we don’t just build, we design for evolution.
            </Typography>
            <div className={classes.btnRow}>
              <Button
                variant="outlined"
                onClick={handleOpen}
                title="Download Brochure"
                rel="noopener"
                className={classes.primaryButton}
                endIcon={<Download />}
              >
                Download Brochure
              </Button>
              <Button
                variant="outlined"
                href="/about-us"
                title="Get a callback"
                rel="noopener"
                className={classes.secondaryButton}
              >
                Know More
              </Button>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default AboutUs;
